import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Paper, IconButton, useMediaQuery, useTheme, Typography } from '@mui/material';
import { Close as CloseIcon, DragIndicator as DragIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useChat } from '../../contexts/ChatContext';
import { useUpload } from '../../contexts/UploadContext';
import { chatAPI } from '../../services/api';
import { chatWebSocket } from '../../services/chatWebSocket';
import { Conversation, ChatMessage } from '../../types/chat';
import { extractErrorMessage } from '../../utils/errorHandler';
import useAlert from '../../hooks/useAlert';
import AlertDialog from '../AlertDialog';
import ChatWidgetButton from './ChatWidgetButton';
import ConversationList from './ConversationList';
import MessageThread from './MessageThread';
import NewConversationDialog from './NewConversationDialog';
import GroupConversationDialog from './GroupConversationDialog';
import GroupSettingsDialog from './GroupSettingsDialog';
import UploadProgressBar from './UploadProgressBar';

// Outlook blue theme
const OUTLOOK_BLUE = '#0078D4';

// Storage key for position persistence
const POSITION_STORAGE_KEY = 'chat_widget_position';
const BUTTON_POSITION_STORAGE_KEY = 'chat_button_position';

// Routes that surface a chat icon in their header navbar — hide the floating button there
const HEADER_CHAT_ROUTES = [
  '/dashboard',
  '/stock',
  '/configuration',
  '/administration',
  '/commercial',
  '/financier',
  '/production',
  '/hr',
  '/messagerie',
];

interface Position {
  x: number;
  y: number;
}

const ChatWidget: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { alertState, showError, hideAlert } = useAlert();

  const { user } = useAuth();
  const location = useLocation();
  const {
    state,
    loadConversations,
    setActiveConversation,
    sendMessage,
    sendTypingIndicator,
    deleteMessage,
    markMessagesAsRead,
    isWidgetOpen: open,
    openWidget,
    closeWidget,
  } = useChat();
  const { uploadFile, getConversationUploads, cancelUpload } = useUpload();

  // All useState hooks must be declared before any conditional returns
  const [newConversationOpen, setNewConversationOpen] = useState(false);
  const [groupConversationOpen, setGroupConversationOpen] = useState(false);
  const [groupSettingsOpen, setGroupSettingsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // Dragging state for widget
  const [widgetPosition, setWidgetPosition] = useState<Position>(() => {
    const saved = localStorage.getItem(POSITION_STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return { x: 24, y: 24 }; // Default: 24px from right and bottom
      }
    }
    return { x: 24, y: 24 };
  });

  // Dragging state for button
  const [buttonPosition, setButtonPosition] = useState<Position>(() => {
    const saved = localStorage.getItem(BUTTON_POSITION_STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return { x: 24, y: 24 };
      }
    }
    return { x: 24, y: 24 };
  });

  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingButton, setIsDraggingButton] = useState(false);
  const dragOffset = useRef<Position>({ x: 0, y: 0 });
  const widgetRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dragStartTime = useRef<number>(0);
  const dragStartPosition = useRef<Position>({ x: 0, y: 0 });
  const hasDragged = useRef<boolean>(false);

  // Clear local messages when user logs out (context handles closing the widget)
  useEffect(() => {
    if (!user) {
      setMessages([]);
    }
  }, [user]);

  // Load conversations when widget opens
  useEffect(() => {
    if (open && state.conversations.length === 0) {
      loadConversations();
    }
  }, [open, state.conversations.length, loadConversations]);

  // Load messages when active conversation changes
  useEffect(() => {
    if (state.activeConversationId) {
      loadMessages(state.activeConversationId);
    } else {
      setMessages([]);
    }
  }, [state.activeConversationId]);

  // Update messages when new message received
  useEffect(() => {
    if (state.activeConversationId) {
      const conversation = state.conversations.find(
        (c) => c.id === state.activeConversationId
      );
      if (conversation?.last_message) {
        setMessages((prev) => {
          const exists = prev.some((m) => m.id === conversation.last_message!.id);
          if (!exists) {
            return [...prev, conversation.last_message!];
          }
          return prev;
        });
      }
    }
  }, [state.conversations, state.activeConversationId]);

  // Subscribe to WebSocket for real-time messages and read receipts
  useEffect(() => {
    const unsubscribe = chatWebSocket.onMessage((message) => {
      if (message.type === 'new_message') {
        const newMsg = message.data as ChatMessage;

        // Add new message to local state if it's for the active conversation
        if (newMsg.conversation_id === state.activeConversationId) {
          setMessages((prev) => {
            // Avoid duplicates
            if (prev.some((m) => m.id === newMsg.id)) {
              return prev;
            }
            return [...prev, newMsg];
          });
        }
      }

      // Handle read receipt - update is_read status for messages I sent
      if (message.type === 'message_read') {
        const { conversation_id, read_at } = message.data;

        // Update is_read for messages I sent in this conversation up to read_at time
        if (conversation_id === state.activeConversationId && read_at && user) {
          setMessages((prev) =>
            prev.map((msg) => {
              // Only mark MY messages as read (messages I sent to others)
              if (msg.sender_id === user.id && new Date(msg.created_at) <= new Date(read_at)) {
                return { ...msg, is_read: true };
              }
              return msg;
            })
          );
        }
      }

      // Handle message deleted - mark message as deleted in real-time
      if (message.type === 'message_deleted') {
        const { message_id, conversation_id } = message.data;

        if (conversation_id === state.activeConversationId) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === message_id
                ? { ...msg, is_deleted: true, content: null, attachments: [] }
                : msg
            )
          );
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [state.activeConversationId]);

  const loadMessages = async (conversationId: number) => {
    setLoadingMessages(true);
    try {
      const response = await chatAPI.getMessages(conversationId);
      setMessages(response.messages);

      // Mark unread messages from others as read
      const unreadMessageIds = response.messages
        .filter((m: ChatMessage) => !m.is_read && m.sender_id !== user?.id)
        .map((m: ChatMessage) => m.id);

      if (unreadMessageIds.length > 0) {
        markMessagesAsRead(unreadMessageIds);
      }
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSelectConversation = (conversation: Conversation) => {
    setActiveConversation(conversation.id);
  };

  const handleNewConversation = () => {
    setNewConversationOpen(true);
  };

  const handleNewGroupConversation = () => {
    setGroupConversationOpen(true);
  };

  const handleSelectUser = async (userId: number) => {
    try {
      // Create or get existing conversation
      const response = await chatAPI.createConversation({ participant_id: userId });
      await loadConversations();
      setActiveConversation(response.id);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    }
  };

  const handleCreateGroup = async (groupName: string, participantIds: number[]) => {
    try {
      const response = await chatAPI.createGroupConversation({ group_name: groupName, participant_ids: participantIds });
      await loadConversations();
      setActiveConversation(response.id);
      setGroupConversationOpen(false);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    }
  };

  const handleOpenGroupSettings = () => {
    setGroupSettingsOpen(true);
  };

  const handleLeaveGroup = async () => {
    if (!state.activeConversationId || !user) return;

    try {
      await chatAPI.removeGroupMember(state.activeConversationId, user.id);
      setActiveConversation(null);
      setGroupSettingsOpen(false);
      await loadConversations();
    } catch (error: any) {
      showError(extractErrorMessage(error));
    }
  };

  const handleConversationUpdate = (updatedConversation: Conversation) => {
    // Reload conversations to reflect the update
    loadConversations();
  };

  const handleSendMessage = async (content: string, files: File[]) => {
    if (!state.activeConversationId) return;

    // If there are files, upload them with progress tracking (non-blocking)
    if (files.length > 0) {
      // Start all uploads in parallel
      const uploadPromises = files.map(file =>
        uploadFile(state.activeConversationId!, file)
      );

      // Wait for all uploads to complete
      try {
        const results = await Promise.all(uploadPromises);
        const attachmentIds = results.filter((id): id is number => id !== null);

        if (attachmentIds.length > 0) {
          // Send message with attachments
          const newMessage = await sendMessage(content || '', attachmentIds);
          if (newMessage) {
            setMessages((prev) => [...prev, newMessage]);
          }
        }
      } catch (error: any) {
        showError(`Échec de l'envoi du fichier: ${extractErrorMessage(error)}`);
      }
    } else if (content.trim()) {
      // Send text-only message
      const newMessage = await sendMessage(content, undefined);
      if (newMessage) {
        setMessages((prev) => [...prev, newMessage]);
      }
    }
  };

  const handleDeleteMessage = async (messageId: number) => {
    const success = await deleteMessage(messageId);
    if (success) {
      // Mark message as deleted instead of removing it (WhatsApp style)
      setMessages((prev) => prev.map((m) =>
        m.id === messageId
          ? { ...m, is_deleted: true, content: null, attachments: [] }
          : m
      ));
    }
  };

  const handleTyping = useCallback(() => {
    sendTypingIndicator();
  }, [sendTypingIndicator]);

  const handleBack = () => {
    setActiveConversation(null);
  };

  const handleClose = () => {
    closeWidget();
    // Reset active conversation so the list shows when reopened
    setActiveConversation(null);
  };

  // ============== Widget Dragging ==============
  const handleWidgetDragStart = (e: React.MouseEvent) => {
    if (isMobile) return;
    e.preventDefault();
    setIsDragging(true);
    dragStartTime.current = Date.now();

    const rect = widgetRef.current?.getBoundingClientRect();
    if (rect) {
      dragOffset.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    }
  };

  const handleWidgetDrag = useCallback((e: MouseEvent) => {
    if (!isDragging || isMobile) return;

    const newX = window.innerWidth - e.clientX - (widgetRef.current?.offsetWidth || 380) + dragOffset.current.x;
    const newY = window.innerHeight - e.clientY - (widgetRef.current?.offsetHeight || 520) + dragOffset.current.y;

    // Keep within bounds
    const boundedX = Math.max(0, Math.min(newX, window.innerWidth - 380));
    const boundedY = Math.max(0, Math.min(newY, window.innerHeight - 520));

    setWidgetPosition({ x: boundedX, y: boundedY });
  }, [isDragging, isMobile]);

  const handleWidgetDragEnd = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(widgetPosition));
    }
  }, [isDragging, widgetPosition]);

  // ============== Button Dragging ==============
  const handleButtonDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (isMobile) return;
    e.preventDefault();
    setIsDraggingButton(true);
    dragStartTime.current = Date.now();
    hasDragged.current = false; // Reset drag flag

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    // Track start position to detect actual movement
    dragStartPosition.current = { x: clientX, y: clientY };

    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      dragOffset.current = {
        x: clientX - rect.left,
        y: clientY - rect.top,
      };
    }
  };

  const handleButtonDrag = useCallback((e: MouseEvent | TouchEvent) => {
    if (!isDraggingButton || isMobile) return;

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    // Check if moved more than 5 pixels (threshold to distinguish click from drag)
    const dx = Math.abs(clientX - dragStartPosition.current.x);
    const dy = Math.abs(clientY - dragStartPosition.current.y);
    if (dx > 5 || dy > 5) {
      hasDragged.current = true;
    }

    const buttonSize = 56; // FAB size
    const newX = window.innerWidth - clientX - buttonSize + dragOffset.current.x;
    const newY = window.innerHeight - clientY - buttonSize + dragOffset.current.y;

    // Keep within bounds
    const boundedX = Math.max(8, Math.min(newX, window.innerWidth - buttonSize - 8));
    const boundedY = Math.max(8, Math.min(newY, window.innerHeight - buttonSize - 8));

    setButtonPosition({ x: boundedX, y: boundedY });
  }, [isDraggingButton, isMobile]);

  const handleButtonDragEnd = useCallback(() => {
    if (isDraggingButton) {
      setIsDraggingButton(false);
      localStorage.setItem(BUTTON_POSITION_STORAGE_KEY, JSON.stringify(buttonPosition));

      // Only open chat if it was a click (no actual movement)
      if (!hasDragged.current) {
        openWidget();
      }
    }
  }, [isDraggingButton, buttonPosition]);

  // Add/remove event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleWidgetDrag);
      window.addEventListener('mouseup', handleWidgetDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleWidgetDrag);
        window.removeEventListener('mouseup', handleWidgetDragEnd);
      };
    }
  }, [isDragging, handleWidgetDrag, handleWidgetDragEnd]);

  useEffect(() => {
    if (isDraggingButton) {
      window.addEventListener('mousemove', handleButtonDrag);
      window.addEventListener('mouseup', handleButtonDragEnd);
      window.addEventListener('touchmove', handleButtonDrag);
      window.addEventListener('touchend', handleButtonDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleButtonDrag);
        window.removeEventListener('mouseup', handleButtonDragEnd);
        window.removeEventListener('touchmove', handleButtonDrag);
        window.removeEventListener('touchend', handleButtonDragEnd);
      };
    }
  }, [isDraggingButton, handleButtonDrag, handleButtonDragEnd]);

  // Close the widget when clicking outside of it (desktop only)
  useEffect(() => {
    if (!open || isMobile) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Ignore clicks inside the widget itself
      if (widgetRef.current && widgetRef.current.contains(target)) return;

      // Ignore clicks inside MUI overlays (dialogs, menus, popovers, backdrops)
      if (target.closest('.MuiModal-root, .MuiPopover-root, .MuiMenu-root, .MuiBackdrop-root')) {
        return;
      }

      // Ignore clicks on a header/floating chat toggle
      if (target.closest('[data-chat-toggle]')) return;

      closeWidget();
      setActiveConversation(null);
    };

    // Defer so the click that opened the widget doesn't immediately close it
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, isMobile, closeWidget, setActiveConversation]);

  // Don't render if not authenticated
  if (!user) {
    return null;
  }

  const activeConversation = state.conversations.find(
    (c) => c.id === state.activeConversationId
  );

  // Get uploads for current conversation
  const currentUploads = state.activeConversationId
    ? getConversationUploads(state.activeConversationId)
    : [];

  const widgetWidth = isMobile ? '100vw' : 380;
  const widgetHeight = isMobile ? '100vh' : 520;

  // Hide the floating button on routes that show a chat icon in their header
  const hideFloatingButton = HEADER_CHAT_ROUTES.some(
    (route) => location.pathname === route || location.pathname.startsWith(route + '/')
  );

  return (
    <>
      {/* Alert Dialog */}
      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
      />

      {/* Floating button - Draggable */}
      {!open && !hideFloatingButton && (
        <ChatWidgetButton
          ref={buttonRef}
          unreadCount={state.unreadCount}
          onClick={() => {}} // Click is handled in drag end to distinguish from drag
          onMouseDown={handleButtonDragStart}
          onTouchStart={handleButtonDragStart}
          position={buttonPosition}
          isDragging={isDraggingButton}
        />
      )}

      {/* Chat widget */}
      {open && (
        <Paper
          ref={widgetRef}
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: isMobile ? 0 : widgetPosition.y,
            right: isMobile ? 0 : widgetPosition.x,
            width: widgetWidth,
            height: widgetHeight,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: isMobile ? 0 : 2,
            overflow: 'hidden',
            zIndex: 1300,
            transition: isDragging ? 'none' : 'box-shadow 0.2s',
          }}
        >
          {/* Widget header - Draggable */}
          <Box
            onMouseDown={handleWidgetDragStart}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: OUTLOOK_BLUE,
              p: 0.5,
              cursor: isMobile ? 'default' : 'move',
              userSelect: 'none',
            }}
          >
            {/* Drag handle indicator */}
            <Box sx={{ display: 'flex', alignItems: 'center', pl: 1 }}>
              {!isMobile && (
                <DragIcon sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 18, mr: 0.5 }} />
              )}
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>
                Messages
              </Typography>
            </Box>
            <Box>
              <IconButton size="small" onClick={handleClose} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
            {/* Show conversation list or message thread based on mobile/desktop */}
            {isMobile ? (
              // Mobile: show either list or thread
              state.activeConversationId && activeConversation ? (
                <MessageThread
                  conversation={activeConversation}
                  messages={messages}
                  currentUserId={user?.id || 0}
                  typingUsers={state.typingUsers}
                  loading={loadingMessages}
                  uploads={currentUploads}
                  onSendMessage={handleSendMessage}
                  onDeleteMessage={handleDeleteMessage}
                  onTyping={handleTyping}
                  onBack={handleBack}
                  onCancelUpload={cancelUpload}
                  showBackButton={true}
                  onOpenGroupSettings={handleOpenGroupSettings}
                  onLeaveGroup={handleLeaveGroup}
                />
              ) : (
                <ConversationList
                  conversations={state.conversations}
                  selectedId={state.activeConversationId || undefined}
                  loading={state.loading}
                  onSelect={handleSelectConversation}
                  onNewConversation={handleNewConversation}
                  onNewGroupConversation={handleNewGroupConversation}
                  onRefresh={loadConversations}
                />
              )
            ) : (
              // Desktop: show list on left, thread on right
              <>
                <Box sx={{ width: '100%' }}>
                  {state.activeConversationId && activeConversation ? (
                    <MessageThread
                      conversation={activeConversation}
                      messages={messages}
                      currentUserId={user?.id || 0}
                      typingUsers={state.typingUsers}
                      loading={loadingMessages}
                      uploads={currentUploads}
                      onSendMessage={handleSendMessage}
                      onDeleteMessage={handleDeleteMessage}
                      onTyping={handleTyping}
                      onBack={handleBack}
                      onCancelUpload={cancelUpload}
                      showBackButton={true}
                      onOpenGroupSettings={handleOpenGroupSettings}
                      onLeaveGroup={handleLeaveGroup}
                    />
                  ) : (
                    <ConversationList
                      conversations={state.conversations}
                      selectedId={state.activeConversationId || undefined}
                      loading={state.loading}
                      onSelect={handleSelectConversation}
                      onNewConversation={handleNewConversation}
                      onNewGroupConversation={handleNewGroupConversation}
                      onRefresh={loadConversations}
                    />
                  )}
                </Box>
              </>
            )}
          </Box>
        </Paper>
      )}

      {/* New conversation dialog */}
      <NewConversationDialog
        open={newConversationOpen}
        onClose={() => setNewConversationOpen(false)}
        onSelectUser={handleSelectUser}
        currentUserId={user?.id || 0}
      />

      {/* Group conversation dialog */}
      <GroupConversationDialog
        open={groupConversationOpen}
        onClose={() => setGroupConversationOpen(false)}
        onCreateGroup={handleCreateGroup}
        currentUserId={user?.id || 0}
      />

      {/* Group settings dialog */}
      {activeConversation?.is_group && (
        <GroupSettingsDialog
          open={groupSettingsOpen}
          onClose={() => setGroupSettingsOpen(false)}
          conversation={activeConversation}
          currentUserId={user?.id || 0}
          onConversationUpdate={handleConversationUpdate}
          onLeaveGroup={handleLeaveGroup}
        />
      )}
    </>
  );
};

export default ChatWidget;
